/**
 * Confirm and alert dialogs on @swtorbit/ui's Modal, replacing native confirm()
 * and alert(). Same props as the shared-components versions they replace.
 */

import type { ReactNode } from 'react';
import { Modal } from '@swtorbit/ui/react';

type Variant = 'primary' | 'danger' | 'warning' | 'info';

/** The confirm button's colour: primary is the default cyan. */
const VARIANT_CLASS: Record<Variant, string> = {
  primary: 'btn',
  danger: 'btn btn--red',
  warning: 'btn btn--amber',
  info: 'btn btn--aqua',
};

const HUE: Partial<Record<Variant, 'red' | 'amber'>> = { danger: 'red', warning: 'amber' };

interface ConfirmModalProps {
  isOpen: boolean;
  onConfirm: () => void;
  /** Called when the user cancels or closes the dialog. */
  onCancel: () => void;
  title: string;
  message: ReactNode;
  confirmText?: string;
  cancelText?: string;
  variant?: Exclude<Variant, 'info'>;
  className?: string;
}

/** A yes/no question. A backdrop click doesn't dismiss it; Escape and Cancel do. */
export function ConfirmModal({
  isOpen,
  onConfirm,
  onCancel,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'primary',
  className,
}: ConfirmModalProps) {
  return (
    <Modal
      open={isOpen}
      onClose={onCancel}
      title={title}
      size="sm"
      hue={HUE[variant]}
      persistent
      className={className}
      footer={
        <>
          <button type="button" onClick={onCancel} className="btn btn--outline btn--slate">
            {cancelText}
          </button>
          <button type="button" onClick={onConfirm} className={VARIANT_CLASS[variant]}>
            {confirmText}
          </button>
        </>
      }
    >
      {typeof message === 'string' ? <p>{message}</p> : message}
    </Modal>
  );
}

interface AlertModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  message: ReactNode;
  buttonText?: string;
  variant?: Variant;
  className?: string;
}

/** A message with one button. */
export function AlertModal({ isOpen, onClose, title, message, buttonText = 'OK', variant = 'primary', className }: AlertModalProps) {
  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title={title}
      size="sm"
      hue={HUE[variant]}
      className={className}
      footer={
        <button type="button" onClick={onClose} className={VARIANT_CLASS[variant]}>
          {buttonText}
        </button>
      }
    >
      {typeof message === 'string' ? <p>{message}</p> : message}
    </Modal>
  );
}
